/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { ActionKernelType } from 'client-code/action-details';
import { ScreenType } from 'client-code/previews';
import { ActionType } from 'client-code/actions';
import { AppUtils } from '../../AppUtils';
import { ServicesClientImpl } from '../../impls/ServicesClientImpl';
import { Log } from '../../logging';
import {
    TAction,
    TActionDetailsList,
    TActionDetailsParams,
    TActionList,
    TActionsParams,
    TSocuLastViewedPositionParams,
    TWatchAction,
} from '../../types';
import { PlaybackContentType } from '../enums/PlaybackContentType';
import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { AppError } from '../../error';

export class SocuDataModelImpl {
    TAG: string = 'SocuDataModelImpl';

    private empty: any = {};

    private static oSocuDataModelImpl: SocuDataModelImpl;

    private service: ServicesClientImpl = new ServicesClientImpl();

    private actionListData: TActionList = this.empty;

    private actionDetailsData: TWatchAction = this.empty;

    private newActionListData: TActionList = this.empty;

    private newActionDetailsData: TWatchAction = this.empty;

    private offerActionError: AppError = this.empty;

    private playbackContentType: PlaybackContentType = PlaybackContentType.LIVE;

    static getInstance(): SocuDataModelImpl {
        if (!SocuDataModelImpl.oSocuDataModelImpl)
            SocuDataModelImpl.oSocuDataModelImpl = new SocuDataModelImpl();
        return SocuDataModelImpl.oSocuDataModelImpl;
    }

    async fetchActionList(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            const actionListResponse = await this.service.fetchOfferActions(actionsParams);
            this.newActionListData = actionListResponse;
            return actionListResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    getActionList(): TActionList {
        if (!AppUtils.isEmpty(this.actionListData)) return this.actionListData;
        return this.empty;
    }

    getSocuAction(): TAction {
        if (!AppUtils.isEmpty(this.actionListData)) {
            return (
                this.actionListData.actionList?.actions?.filter(
                    (action) => action.actionType === ActionType.WatchFromProviderAction,
                )[0] || this.empty
            );
        }
        return this.empty;
    }

    getNewActionList(): TActionList {
        if (!AppUtils.isEmpty(this.newActionListData)) return this.newActionListData;
        return this.empty;
    }

    getNewSocuAction(): TAction {
        if (!AppUtils.isEmpty(this.newActionListData)) {
            return (
                this.newActionListData.actionList?.actions?.filter(
                    (action) => action.actionType === ActionType.WatchFromProviderAction,
                )[0] || this.empty
            );
        }
        return this.empty;
    }

    async fetchActionDetails(
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        try {
            const actionDetailsResponse =
                await this.service.fetchOttOfferActionDetails(actionDetailsParams);
            this.newActionDetailsData =
                actionDetailsResponse?.actionDetails?.watchActionsList?.filter(
                    (action) =>
                        action.kernel.actionKernelType.toLowerCase() ===
                        ActionKernelType.WatchFromSoCuActionKernel.toLowerCase(),
                )[0] || this.empty;
            return actionDetailsResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    async fetchLastSavedPosition(
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<{ watchAction: TWatchAction; actionDetailsNetworkCallId?: number }> {
        const { socuOfferId, isCatchUp, isStartOver } = this.getNewSocuAction()?.kernel;
        const actionDetailsParams: TActionDetailsParams = {
            isCatchUp: !!isCatchUp,
            isStartOver: !!isStartOver,
            offerId: socuOfferId,
            screenType,
            actionDetailsNetworkCallId: networkCallId,
        };
        try {
            const { actionDetailsNetworkCallId } = await this.fetchActionDetails(
                actionDetailsParams,
            );
            const watchAction: TWatchAction = this.getNewActionDetails();
            return { watchAction, actionDetailsNetworkCallId };
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    getActionDetails(): TWatchAction {
        if (!AppUtils.isEmpty(this.actionDetailsData)) return this.actionDetailsData;
        return this.empty;
    }

    getNewActionDetails(): TWatchAction {
        if (!AppUtils.isEmpty(this.newActionDetailsData)) return this.newActionDetailsData;
        return this.empty;
    }

    getLastViewedPosition(): number {
        if (!AppUtils.isEmpty(this.actionDetailsData)) {
            return this.actionDetailsData.kernel.savedPosition || 0;
        }
        return 0;
    }

    updateLastViewedPosition(currentTime: number, startTime: number, screenType: string): void {
        try {
            const { collectionId, partnerOfferId, partnerId, contentId, duration } =
                this.getActionDetails()?.kernel || {};
            if (currentTime && contentId && partnerOfferId) {
                let lastViewedPosition = Math.round(currentTime / MILLISECONDS_IN_A_SECOND);
                if (this.isStartOver()) {
                    lastViewedPosition = Math.round(
                        (currentTime - startTime) / MILLISECONDS_IN_A_SECOND,
                    );
                }
                const socuLVPParams: TSocuLastViewedPositionParams = {
                    lastViewedPosition,
                    partnerOfferId,
                    providerPartnerId: partnerId,
                    screenType,
                    contentId,
                    collectionId,
                    duration,
                };
                this.service.setSocuLastViewedPosition(socuLVPParams);
            }
        } catch (error: any) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    getOfferActionError(): AppError {
        return this.offerActionError;
    }

    getPlaybackContentType(): PlaybackContentType {
        return this.playbackContentType;
    }

    isStartOver(): boolean {
        return (
            this.playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
            this.playbackContentType === PlaybackContentType.STARTOVER_RESUME
        );
    }

    isCatchUp(): boolean {
        return (
            this.playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
            this.playbackContentType === PlaybackContentType.CATCHUP_RESUME
        );
    }

    isSocu(): boolean {
        return this.isCatchUp() || this.isStartOver();
    }

    setOfferActionError(error: AppError) {
        this.offerActionError = error;
    }

    setPlaybackContentType(playbackContentType: PlaybackContentType) {
        this.playbackContentType = playbackContentType;
    }

    setSocuData(playbackType: PlaybackContentType): void {
        this.actionListData = this.newActionListData;
        this.actionDetailsData = this.newActionDetailsData;
        this.playbackContentType = playbackType;
        this.resetNewSocuData();
    }

    resetSocuData(): void {
        this.resetNewSocuData();
        this.playbackContentType = PlaybackContentType.LIVE;
        this.actionListData = this.empty;
        this.actionDetailsData = this.empty;
    }

    resetNewSocuData(): void {
        this.newActionListData = this.empty;
        this.newActionDetailsData = this.empty;
    }
}
